import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Mic, MicOff, IndianRupee, Plus, Sparkles, Check, X,
  KeyRound, LockKeyhole, FileText, CheckSquare, CreditCard,
  Files, MoreHorizontal, LayoutGrid, ArrowRight, RefreshCw,
  Volume2, AlertCircle, Eye, EyeOff, ShieldCheck, Tag
} from 'lucide-react';
import { parseNaturalLanguage, createTransaction, listAccounts, listCategories } from '../../api/expenses';
import { createNote } from '../../api/notepad';
import api from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { useApplications } from '../../hooks/useApplications';

// Multilingual speech parser client fallback
function parseSpokenExpense(text) {
  const sLower = text.toLowerCase().trim();
  let amt = 0;
  const numMatch = sLower.match(/(?:₹|rs\.?|inr)?\s*([0-9]+(?:\.[0-9]{1,2})?)\s*(?:rupay|rupaye|rupee|rupees|rs|ka|ke|pe|रुपए|रुपये|का|की|के)?/i);
  if (numMatch) {
    amt = parseFloat(numMatch[1]);
  } else {
    if (/(?:^|\s)(?:बीस|bees|bis)(?:$|\s)/i.test(sLower)) amt = 20;
    else if (/(?:^|\s)(?:तीस|tees|tis)(?:$|\s)/i.test(sLower)) amt = 30;
    else if (/(?:^|\s)(?:चालीस|chalis|chaalis)(?:$|\s)/i.test(sLower)) amt = 40;
    else if (/(?:^|\s)(?:पचास|pachas|pachaas)(?:$|\s)/i.test(sLower)) amt = 50;
    else if (/(?:^|\s)(?:सौ|sau|so)(?:$|\s)/i.test(sLower)) amt = 100;
    else if (/(?:^|\s)(?:हजार|hazar|hazaar)(?:$|\s)/i.test(sLower)) amt = 1000;
    else if (/(?:^|\s)(?:दस|das)(?:$|\s)/i.test(sLower)) amt = 10;
  }

  let cat = 'Other';
  let desc = text;

  if (/आलू|aalu|टमाटर|tamatar|ककड़ी|kakdi|सब्जी|sabzi|दूध|milk|food|chai|चाय|samosa|khana/i.test(sLower)) {
    cat = 'Food';
    desc = /chai|चाय/i.test(sLower) ? 'Chai' : /samosa/i.test(sLower) ? 'Samosa' : /petrol/i.test(sLower) ? 'Petrol' : 'Food & Groceries';
  } else if (/petrol|diesel|fuel|rickshaw|riksha|cab|uber|ola|bus|train|पैट्रोल|पेट्रोल/i.test(sLower)) {
    cat = 'Transport';
    desc = 'Petrol / Transport';
  } else if (/shirt|clothes|shopping|pant|jeans|shoes/i.test(sLower)) {
    cat = 'Shopping';
    desc = 'Clothing / Shopping';
  }

  return {
    merchant: desc,
    amount: amt,
    category: cat,
    type: 'EXPENSE',
    date: new Date().toISOString().split('T')[0],
  };
}

// Google Gemini Iconic 4-pointed Star SVG with Multi-Color Aurora Gradient
export function GeminiSparkleIcon({ size = 24, className = '', color = 'white' }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={{ overflow: 'visible' }}
    >
      <defs>
        <linearGradient id="gemini-multi-aurora" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#4285F4" />
          <stop offset="25%" stopColor="#9B72CF" />
          <stop offset="50%" stopColor="#EC4899" />
          <stop offset="75%" stopColor="#F59E0B" />
          <stop offset="100%" stopColor="#00C9FF" />
        </linearGradient>
        <filter id="gemini-aurora-glow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="1.2" result="blur" />
          <feComposite in="SourceGraphic" in2="blur" operator="over" />
        </filter>
      </defs>
      {/* Primary Gemini 4-Pointed Star */}
      <path
        d="M12 0C12 6.62742 6.62742 12 0 12C6.62742 12 12 17.3726 12 24C12 17.3726 17.3726 12 24 12C17.3726 12 12 6.62742 12 0Z"
        fill={color === 'white' ? '#ffffff' : 'url(#gemini-multi-aurora)'}
        filter={color === 'white' ? undefined : 'url(#gemini-aurora-glow)'}
      />
      {/* Secondary Companion Star */}
      <path
        d="M19.5 0.5C19.5 2.433 17.933 4 16 4C17.933 4 19.5 5.567 19.5 7.5C19.5 5.567 21.067 4 23 4C21.067 4 19.5 2.433 19.5 0.5Z"
        fill="#FFE082"
        opacity="0.95"
      />
    </svg>
  );
}

export default function UniversalActionFab({ onRefreshData }) {
  const { hasPermission } = useApplications();
  const { user } = useAuth();

  // App Permission gates
  const canExpenses = hasPermission('expense-tracker');
  const canPasswords = hasPermission('passwords');
  const canNotes = hasPermission('notes') || hasPermission('notepad');
  const canTasks = hasPermission('tasks');
  const canCards = hasPermission('cards');
  const canDocuments = hasPermission('documents');

  // Floating dock state
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [showTooltip, setShowTooltip] = useState(true);

  // Voice AI Expense state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingPulse, setRecordingPulse] = useState(false);
  const [spokenTranscript, setSpokenTranscript] = useState('');
  const [aiConfirmOpen, setAiConfirmOpen] = useState(false);
  const [aiCandidate, setAiCandidate] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [holdProgress, setHoldProgress] = useState(0);

  // Quick Action Drawers/Modals
  const [quickExpenseOpen, setQuickExpenseOpen] = useState(false);
  const [quickPasswordOpen, setQuickPasswordOpen] = useState(false);
  const [quickNoteOpen, setQuickNoteOpen] = useState(false);
  const [quickTaskOpen, setQuickTaskOpen] = useState(false);
  const [feedbackToast, setFeedbackToast] = useState(null);

  // Reference data for expense accounts/categories
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);

  // Press & hold timer references
  const pressTimerRef = useRef(null);
  const holdProgressIntervalRef = useRef(null);
  const holdStartTimeRef = useRef(0);
  const isLongPressTriggeredRef = useRef(false);
  const isLongPressRef = useRef(false);
  const recognitionRef = useRef(null);

  const showToast = (msg, type = 'success') => {
    setFeedbackToast({ msg, type });
    setTimeout(() => setFeedbackToast(null), 3500);
  };

  useEffect(() => {
    if (canExpenses) {
      listAccounts().then(res => setAccounts(res.data || [])).catch(() => {});
      listCategories().then(res => setCategories(res.data || [])).catch(() => {});
    }
  }, [canExpenses]);

  // ─── SPEECH RECOGNITION SETUP ───
  const startVoiceRecording = useCallback(() => {
    isLongPressRef.current = true;
    setSpokenTranscript('');

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      showToast('Speech recognition not supported in this browser', 'error');
      setQuickExpenseOpen(true);
      return;
    }

    try {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'hi-IN, en-IN, gu-IN';

      recognition.onstart = () => {
        setIsRecording(true);
        setRecordingPulse(true);
        if (navigator.vibrate) navigator.vibrate(50);
      };

      recognition.onresult = (e) => {
        let transcript = '';
        for (let i = 0; i < e.results.length; i++) {
          transcript += e.results[i][0].transcript;
        }
        setSpokenTranscript(transcript);
      };

      recognition.onerror = (e) => {
        console.warn('Speech recognition error:', e.error);
        setIsRecording(false);
        setRecordingPulse(false);
      };

      recognition.onend = () => {
        setIsRecording(false);
        setRecordingPulse(false);
      };

      recognitionRef.current = recognition;
      recognition.start();
    } catch (err) {
      console.error('Failed to start speech recognition:', err);
      setIsRecording(false);
      setRecordingPulse(false);
    }
  }, []);

  const stopVoiceRecordingAndProcess = useCallback(async () => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {}
    }
    setIsRecording(false);
    setRecordingPulse(false);

    const textToProcess = spokenTranscript.trim();
    if (!textToProcess) {
      return;
    }

    // Process spoken text
    setAiLoading(true);
    try {
      // First try backend NLP parse
      const res = await parseNaturalLanguage(textToProcess);
      if (res?.data?.extracted && res.data.extracted.amount > 0) {
        setAiCandidate(res.data.extracted);
        setAiConfirmOpen(true);
      } else {
        // Fallback to smart client parser
        const parsed = parseSpokenExpense(textToProcess);
        if (parsed.amount > 0) {
          setAiCandidate(parsed);
          setAiConfirmOpen(true);
        } else {
          showToast(`No expense amount detected in: "${textToProcess}"`, 'info');
        }
      }
    } catch {
      const parsed = parseSpokenExpense(textToProcess);
      if (parsed.amount > 0) {
        setAiCandidate(parsed);
        setAiConfirmOpen(true);
      } else {
        showToast(`No expense amount detected in: "${textToProcess}"`, 'info');
      }
    } finally {
      setAiLoading(false);
    }
  }, [spokenTranscript]);

  // Press & Hold Handlers for Expense Button (Matching VoiceExpenseFab)
  const handleExpenseMouseDown = (e) => {
    holdStartTimeRef.current = Date.now();
    isLongPressTriggeredRef.current = false;
    setHoldProgress(0);

    const startTime = Date.now();
    const duration = 380; // ms to activate voice

    if (holdProgressIntervalRef.current) clearInterval(holdProgressIntervalRef.current);
    holdProgressIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const pct = Math.min(100, Math.round((elapsed / duration) * 100));
      setHoldProgress(pct);
      if (pct >= 100) {
        clearInterval(holdProgressIntervalRef.current);
      }
    }, 25);

    if (pressTimerRef.current) clearTimeout(pressTimerRef.current);
    pressTimerRef.current = setTimeout(() => {
      isLongPressTriggeredRef.current = true;
      setHoldProgress(100);
      if (navigator.vibrate) navigator.vibrate([60, 40, 60]);
      startVoiceRecording();
    }, duration);
  };

  const handleExpenseMouseUp = () => {
    if (pressTimerRef.current) {
      clearTimeout(pressTimerRef.current);
      pressTimerRef.current = null;
    }
    if (holdProgressIntervalRef.current) {
      clearInterval(holdProgressIntervalRef.current);
      holdProgressIntervalRef.current = null;
    }
    setHoldProgress(0);

    const elapsed = Date.now() - holdStartTimeRef.current;

    if (!isLongPressTriggeredRef.current && elapsed < 380) {
      // Single Tap: Open quick add expense drawer
      if (navigator.vibrate) navigator.vibrate(30);
      setQuickExpenseOpen(true);
    } else if (isLongPressTriggeredRef.current) {
      // User held and released: Stop voice recording and process
      setTimeout(() => {
        stopVoiceRecordingAndProcess();
      }, 350);
    }
    isLongPressTriggeredRef.current = false;
  };

  // Confirm and save AI expense
  const handleConfirmAiExpense = async () => {
    if (!aiCandidate) return;
    try {
      const defaultAcc = accounts[0];
      const defaultCat = categories.find(c => c.name.toLowerCase().includes((aiCandidate.category || '').toLowerCase())) || categories[0];

      await createTransaction({
        type: 'EXPENSE',
        amount: Number(aiCandidate.amount),
        currency: 'INR',
        merchant: aiCandidate.merchant || 'Voice Expense',
        description: aiCandidate.merchant || 'Voice Expense',
        category_id: defaultCat?.id,
        account_id: defaultAcc?.id,
        transaction_date: aiCandidate.date || new Date().toISOString().split('T')[0],
        payment_method: 'CASH',
        source: 'VOICE_AI',
      });

      showToast(`Logged ₹${aiCandidate.amount} (${aiCandidate.merchant}) via Voice AI!`);
      setAiConfirmOpen(false);
      setAiCandidate(null);
      if (onRefreshData) onRefreshData();
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to record expense', 'error');
    }
  };

  // If user has no permissions for any of these, don't render floating dock
  if (!canExpenses && !canPasswords && !canNotes && !canTasks && !canCards && !canDocuments) {
    return null;
  }

  return (
    <>
      {/* Toast Feedback */}
      {feedbackToast && (
        <div className={`et-toast et-toast-${feedbackToast.type}`} style={{ zIndex: 100002 }}>
          {feedbackToast.type === 'error' ? <AlertCircle size={16} /> : <Check size={16} />}
          <span>{feedbackToast.msg}</span>
        </div>
      )}

      {/* Voice Recording Waveform Overlay */}
      {isRecording && (
        <div className="voice-active-overlay animate-fade-in">
          <div className="voice-active-modal glass-panel">
            <div className="voice-wave-ripple" />
            <div className="voice-mic-hero">
              <Mic size={32} color="#ffffff" />
            </div>
            <h3 className="voice-active-title">Listening to Expense...</h3>
            <p className="voice-active-hint">
              Speak naturally: "20 rupay chai", "500 petrol", "1500 grocery"...
            </p>
            {spokenTranscript && (
              <div className="voice-transcript-bubble">
                "{spokenTranscript}"
              </div>
            )}
            <span className="voice-release-text">Release button when finished</span>
          </div>
        </div>
      )}

      {/* ─── BOTTOM RIGHT FLOATING DOCK ─── */}
      <div className="universal-fab-dock" role="toolbar" aria-label="Quick Actions">
        
        {/* Expanded iOS Radial / Staggered Action Menu */}
        {isMoreOpen && (
          <div className="universal-fab-expanded-menu animate-slide-up">
            {/* Quick Note */}
            {canNotes && (
              <div className="fab-item-row">
                <span className="fab-item-label">Quick Note</span>
                <button
                  type="button"
                  className="fab-action-btn fab-color-amber"
                  onClick={() => { setIsMoreOpen(false); setQuickNoteOpen(true); }}
                  title="New Secure Note"
                  aria-label="New Secure Note"
                >
                  <FileText size={20} />
                </button>
              </div>
            )}

            {/* Quick Task */}
            {canTasks && (
              <div className="fab-item-row">
                <span className="fab-item-label">New Task</span>
                <button
                  type="button"
                  className="fab-action-btn fab-color-purple"
                  onClick={() => { setIsMoreOpen(false); setQuickTaskOpen(true); }}
                  title="New Task"
                  aria-label="New Task"
                >
                  <CheckSquare size={20} />
                </button>
              </div>
            )}

            {/* Quick Card */}
            {canCards && (
              <div className="fab-item-row">
                <span className="fab-item-label">Add Card</span>
                <button
                  type="button"
                  className="fab-action-btn fab-color-rose"
                  onClick={() => { setIsMoreOpen(false); window.location.href = '/apps/cards'; }}
                  title="Store Card"
                  aria-label="Store Card"
                >
                  <CreditCard size={20} />
                </button>
              </div>
            )}

            {/* Quick Document */}
            {canDocuments && (
              <div className="fab-item-row">
                <span className="fab-item-label">Documents</span>
                <button
                  type="button"
                  className="fab-action-btn fab-color-blue"
                  onClick={() => { setIsMoreOpen(false); window.location.href = '/apps/documents'; }}
                  title="Upload Document"
                  aria-label="Upload Document"
                >
                  <Files size={20} />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Animated Tooltip / Pill Badge (matching Expense Tracker FAB) */}
        {showTooltip && canExpenses && !isRecording && (
          <div
            className="v-fab-tooltip animate-pop-in"
            onClick={(e) => {
              e.stopPropagation();
              setQuickExpenseOpen(true);
            }}
          >
            <span className="v-fab-tooltip-icon">✨</span>
            <div className="v-fab-tooltip-text">
              <strong>Tap:</strong> Write Expense &bull; <strong>Hold:</strong> Voice AI
            </div>
            <button
              type="button"
              className="v-fab-tooltip-close"
              onClick={(e) => {
                e.stopPropagation();
                setShowTooltip(false);
              }}
              aria-label="Close hint"
            >
              <X size={12} />
            </button>
          </div>
        )}

        {/* Primary Visible Button Row: [Expense FAB] [Password FAB] [More Button] */}
        <div className="universal-fab-primary-row">

          {/* 1. EXPENSE FAB (Voice AI + Quick Add - identical to Expense Tracker FAB) */}
          {canExpenses && (
            <div className="fab-btn-wrap">
              <button
                type="button"
                className={`v-fab-button v-fab-expense-btn ${holdProgress > 0 ? 'v-fab-holding' : ''} ${isRecording ? 'v-fab-mic-mode' : 'v-fab-expense-mode'}`}
                style={{
                  borderRadius: '50%',
                  WebkitBorderRadius: '50%',
                  width: '58px',
                  height: '58px',
                  minWidth: '58px',
                  minHeight: '58px',
                  maxWidth: '58px',
                  maxHeight: '58px',
                  aspectRatio: '1 / 1',
                  overflow: 'hidden',
                  padding: 0
                }}
                onMouseDown={handleExpenseMouseDown}
                onMouseUp={handleExpenseMouseUp}
                onMouseLeave={handleExpenseMouseUp}
                onTouchStart={handleExpenseMouseDown}
                onTouchEnd={handleExpenseMouseUp}
                onTouchCancel={handleExpenseMouseUp}
                onContextMenu={(e) => e.preventDefault()}
                title="Tap to Add Expense | Hold for Voice Assistant"
                aria-label="Tap to Add Expense, Hold to activate Voice Expense"
              >
                {/* Glowing Aura Rings */}
                {(holdProgress > 0 || isRecording) && (
                  <>
                    <div className="v-fab-aura-ring v-fab-aura-1" />
                    <div className="v-fab-aura-ring v-fab-aura-2" />
                  </>
                )}

                {/* SVG Circular Hold Progress Ring */}
                <svg className="v-fab-progress-svg" viewBox="0 0 68 68">
                  <circle
                    className="v-fab-progress-bg"
                    cx="34"
                    cy="34"
                    r="30"
                    strokeWidth="3.5"
                  />
                  <circle
                    className="v-fab-progress-fill"
                    cx="34"
                    cy="34"
                    r="30"
                    strokeWidth="3.5"
                    style={{
                      strokeDashoffset: `${188.4 - (188.4 * holdProgress) / 100}`,
                    }}
                  />
                </svg>

                {/* Morphing / Alternating Animated Icons */}
                <div className="v-fab-icon-wrapper">
                  <div className={`v-fab-icon-flip ${isRecording ? 'flipped' : ''}`}>
                    {/* Front: Expense Icon (Indian Rupee with Plus badge) */}
                    <div className="v-fab-icon-face v-fab-icon-front">
                      <IndianRupee size={24} strokeWidth={2.4} color="#ffffff" />
                      <span className="v-fab-mini-badge">+</span>
                    </div>
                    {/* Back: Voice Icon (Microphone with pulsing red dot) */}
                    <div className="v-fab-icon-face v-fab-icon-back">
                      <Mic size={24} strokeWidth={2.4} color="#ffffff" />
                      <span className="v-fab-mini-pulse" />
                    </div>
                  </div>
                </div>
              </button>
            </div>
          )}

          {/* 2. PASSWORD VAULT FAB (Quick Add Password) */}
          {canPasswords && (
            <div className="fab-btn-wrap">
              <button
                type="button"
                className="v-fab-button v-fab-vault-btn"
                style={{
                  borderRadius: '50%',
                  WebkitBorderRadius: '50%',
                  width: '58px',
                  height: '58px',
                  minWidth: '58px',
                  minHeight: '58px',
                  maxWidth: '58px',
                  maxHeight: '58px',
                  aspectRatio: '1 / 1',
                  overflow: 'hidden',
                  padding: 0
                }}
                onClick={() => setQuickPasswordOpen(true)}
                title="Add Password to Vault"
                aria-label="Add Password to Vault"
              >
                <div className="v-fab-icon-wrapper">
                  <div className="v-fab-icon-face v-fab-icon-front">
                    <KeyRound size={24} strokeWidth={2.4} color="#ffffff" />
                    <span className="v-fab-mini-badge" style={{ color: '#0284c7' }}>+</span>
                  </div>
                </div>
              </button>
            </div>
          )}

          {/* 3. MORE APPS BUTTON */}
          <div className="fab-btn-wrap">
            <button
              type="button"
              className={`v-fab-button v-fab-more-btn ${isMoreOpen ? 'v-fab-more-open' : ''}`}
              style={{
                borderRadius: '50%',
                WebkitBorderRadius: '50%',
                width: '58px',
                height: '58px',
                minWidth: '58px',
                minHeight: '58px',
                maxWidth: '58px',
                maxHeight: '58px',
                aspectRatio: '1 / 1',
                overflow: 'hidden',
                padding: 0
              }}
              onClick={() => setIsMoreOpen(prev => !prev)}
              title={isMoreOpen ? 'Close Actions' : 'More App Actions'}
              aria-label={isMoreOpen ? 'Close Actions' : 'More App Actions'}
            >
              <div className="v-fab-icon-wrapper">
                <div className="v-fab-icon-face v-fab-icon-front">
                  {isMoreOpen ? (
                    <X size={24} strokeWidth={2.4} color="#ffffff" />
                  ) : (
                    <>
                      <Sparkles size={24} strokeWidth={2.4} color="#ffffff" />
                      <span className="v-fab-mini-badge" style={{ color: '#6366f1', fontSize: '10px' }}>✦</span>
                    </>
                  )}
                </div>
              </div>
            </button>
          </div>

        </div>
      </div>

      {/* ─── MODAL 1: QUICK ADD PASSWORD DRAWER ─── */}
      {quickPasswordOpen && (
        <div className="card-drawer-wrapper" role="presentation" style={{ zIndex: 100005 }}>
          <div className="card-drawer-overlay animate-fade-in" onClick={() => setQuickPasswordOpen(false)} aria-hidden="true" />
          <aside className="card-drawer et-drawer animate-slide-left" role="dialog" aria-modal="true" aria-label="Add Password">
            <div className="card-drawer-header et-drawer-header">
              <div className="card-drawer-header-left">
                <div className="card-drawer-icon-badge" style={{ background: 'rgba(13, 148, 136, 0.15)', color: '#0d9488' }}>
                  <LockKeyhole size={18} />
                </div>
                <div className="card-drawer-titles">
                  <h2 className="card-drawer-title">New Password Item</h2>
                  <p className="card-drawer-subtitle">Zero-knowledge encrypted credential</p>
                </div>
              </div>
              <button type="button" className="btn-icon" onClick={() => setQuickPasswordOpen(false)}><X size={18} /></button>
            </div>

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.target;
                const title = form.title.value.trim();
                const username = form.username.value.trim();
                const password = form.password.value;
                const url = form.url.value.trim();

                try {
                  // Get default vault
                  const vaultsRes = await api.get('/vaults');
                  const vaultId = vaultsRes.data?.[0]?.id;
                  if (!vaultId) throw new Error('No vault found');

                  await api.post(`/vaults/${vaultId}/items`, {
                    item_type: 'LOGIN',
                    encrypted_data: JSON.stringify({
                      title,
                      username,
                      password,
                      url,
                      category: 'Logins',
                    }),
                  });

                  showToast(`Password for "${title}" saved to vault!`);
                  setQuickPasswordOpen(false);
                  if (onRefreshData) onRefreshData();
                } catch (err) {
                  // Fallback: direct navigate to vault if local master key encryption is needed
                  window.location.href = `/vault/all?action=create&title=${encodeURIComponent(title)}`;
                }
              }}
              style={{ display: 'contents' }}
            >
              <div className="card-drawer-body et-drawer-body">
                <div className="et-form-grid">
                  <div className="et-form-field">
                    <label>Title / Service Name*</label>
                    <input name="title" type="text" required placeholder="e.g. Google, GitHub, Netflix" className="et-input" autoFocus />
                  </div>
                  <div className="et-form-field">
                    <label>Username / Email*</label>
                    <input name="username" type="text" required placeholder="name@example.com" className="et-input" />
                  </div>
                  <div className="et-form-field">
                    <label>Password*</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input id="quick-pass-input" name="password" type="text" required placeholder="Strong password" className="et-input" />
                      <button
                        type="button"
                        className="et-btn et-btn-secondary"
                        onClick={() => {
                          const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*';
                          const vals = new Uint32Array(20);
                          window.crypto.getRandomValues(vals);
                          const pass = Array.from(vals, v => alphabet[v % alphabet.length]).join('');
                          const el = document.getElementById('quick-pass-input');
                          if (el) el.value = pass;
                        }}
                        title="Generate Password"
                      >
                        ⚡
                      </button>
                    </div>
                  </div>
                  <div className="et-form-field">
                    <label>Website URL</label>
                    <input name="url" type="url" placeholder="https://..." className="et-input" />
                  </div>
                </div>
              </div>
              <div className="card-drawer-footer et-drawer-footer">
                <button type="button" className="et-btn et-btn-secondary" onClick={() => setQuickPasswordOpen(false)}>Cancel</button>
                <button type="submit" className="et-btn et-btn-primary">Save to Vault</button>
              </div>
            </form>
          </aside>
        </div>
      )}

      {/* ─── MODAL 2: QUICK ADD EXPENSE DRAWER ─── */}
      {quickExpenseOpen && (
        <div className="card-drawer-wrapper" role="presentation" style={{ zIndex: 100005 }}>
          <div className="card-drawer-overlay animate-fade-in" onClick={() => setQuickExpenseOpen(false)} aria-hidden="true" />
          <aside className="card-drawer et-drawer animate-slide-left" role="dialog" aria-modal="true" aria-label="Add Expense">
            <div className="card-drawer-header et-drawer-header">
              <div className="card-drawer-header-left">
                <div className="card-drawer-icon-badge" style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#10b981' }}>
                  <IndianRupee size={18} />
                </div>
                <div className="card-drawer-titles">
                  <h2 className="card-drawer-title">Add Expense</h2>
                  <p className="card-drawer-subtitle">Log transaction or tap-and-hold for Voice AI</p>
                </div>
              </div>
              <button type="button" className="btn-icon" onClick={() => setQuickExpenseOpen(false)}><X size={18} /></button>
            </div>

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.target;
                const amount = Number(form.amount.value);
                const merchant = form.merchant.value.trim();
                const category_id = form.category_id.value || undefined;
                const account_id = form.account_id.value || undefined;

                try {
                  await createTransaction({
                    type: 'EXPENSE',
                    amount,
                    currency: 'INR',
                    merchant,
                    description: merchant,
                    category_id,
                    account_id,
                    transaction_date: form.date.value || new Date().toISOString().split('T')[0],
                    payment_method: 'CASH',
                    source: 'MANUAL',
                  });

                  showToast(`Expense of ₹${amount} logged!`);
                  setQuickExpenseOpen(false);
                  if (onRefreshData) onRefreshData();
                } catch (err) {
                  showToast(err.response?.data?.message || 'Failed to add expense', 'error');
                }
              }}
              style={{ display: 'contents' }}
            >
              <div className="card-drawer-body et-drawer-body">
                <div className="et-form-grid">
                  <div className="et-form-field">
                    <label>Amount (₹)*</label>
                    <input name="amount" type="number" step="0.01" required placeholder="500" className="et-input" autoFocus />
                  </div>
                  <div className="et-form-field">
                    <label>Item / Merchant / Description*</label>
                    <input name="merchant" type="text" required placeholder="e.g. Petrol, Groceries, Dinner" className="et-input" />
                  </div>
                  <div className="et-form-field">
                    <label>Category</label>
                    <select name="category_id" className="et-select">
                      <option value="">Select Category</option>
                      {categories.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="et-form-field">
                    <label>Payment Account</label>
                    <select name="account_id" className="et-select">
                      <option value="">Select Account</option>
                      {accounts.map(a => (
                        <option key={a.id} value={a.id}>{a.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="et-form-field">
                    <label>Date</label>
                    <input name="date" type="date" defaultValue={new Date().toISOString().split('T')[0]} className="et-input" />
                  </div>
                </div>
              </div>
              <div className="card-drawer-footer et-drawer-footer">
                <button type="button" className="et-btn et-btn-secondary" onClick={() => setQuickExpenseOpen(false)}>Cancel</button>
                <button type="submit" className="et-btn et-btn-primary">Record Expense</button>
              </div>
            </form>
          </aside>
        </div>
      )}

      {/* ─── MODAL 3: CONFIRM VOICE AI PARSED EXPENSE ─── */}
      {aiConfirmOpen && aiCandidate && (
        <div className="card-drawer-wrapper" role="presentation" style={{ zIndex: 100010 }}>
          <div className="card-drawer-overlay animate-fade-in" onClick={() => setAiConfirmOpen(false)} aria-hidden="true" />
          <div className="voice-confirm-modal glass-panel animate-scale-in">
            <div className="voice-confirm-header">
              <div className="card-drawer-icon-badge" style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#10b981' }}>
                <Sparkles size={20} />
              </div>
              <div>
                <h3 style={{ margin: 0, color: '#ffffff' }}>Voice AI Expense Detected</h3>
                <span style={{ fontSize: '0.74rem', color: '#94a3b8' }}>Review and confirm transaction</span>
              </div>
            </div>

            <div className="voice-confirm-body">
              <div className="voice-confirm-row">
                <span className="voice-confirm-key">Item:</span>
                <strong className="voice-confirm-val">{aiCandidate.merchant}</strong>
              </div>
              <div className="voice-confirm-row">
                <span className="voice-confirm-key">Amount:</span>
                <strong className="voice-confirm-val" style={{ color: '#ef4444', fontSize: '1.2rem' }}>
                  ₹{Number(aiCandidate.amount).toLocaleString('en-IN')}
                </strong>
              </div>
              <div className="voice-confirm-row">
                <span className="voice-confirm-key">Category:</span>
                <span className="voice-confirm-pill">{aiCandidate.category || 'Food'}</span>
              </div>
            </div>

            <div className="voice-confirm-footer">
              <button type="button" className="et-btn et-btn-secondary" onClick={() => setAiConfirmOpen(false)}>
                Cancel
              </button>
              <button type="button" className="et-btn et-btn-primary" onClick={handleConfirmAiExpense}>
                <Check size={16} /> Confirm & Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── MODAL 4: QUICK ADD NOTE DRAWER ─── */}
      {quickNoteOpen && (
        <div className="card-drawer-wrapper" role="presentation" style={{ zIndex: 100005 }}>
          <div className="card-drawer-overlay animate-fade-in" onClick={() => setQuickNoteOpen(false)} aria-hidden="true" />
          <aside className="card-drawer et-drawer animate-slide-left" role="dialog" aria-modal="true" aria-label="Quick Note">
            <div className="card-drawer-header et-drawer-header">
              <div className="card-drawer-header-left">
                <div className="card-drawer-icon-badge" style={{ background: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b' }}>
                  <FileText size={18} />
                </div>
                <div className="card-drawer-titles">
                  <h2 className="card-drawer-title">Quick Note</h2>
                  <p className="card-drawer-subtitle">Client-encrypted markdown note</p>
                </div>
              </div>
              <button type="button" className="btn-icon" onClick={() => setQuickNoteOpen(false)}><X size={18} /></button>
            </div>

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.target;
                const title = form.title.value.trim();
                const content = form.content.value.trim();

                try {
                  await createNote({
                    title,
                    content,
                    category: 'General',
                  });
                  showToast(`Note "${title}" created!`);
                  setQuickNoteOpen(false);
                  if (onRefreshData) onRefreshData();
                } catch {
                  // Fallback: redirect to notes app with prefill
                  window.location.href = `/apps/notes?title=${encodeURIComponent(title)}`;
                }
              }}
              style={{ display: 'contents' }}
            >
              <div className="card-drawer-body et-drawer-body">
                <div className="et-form-grid">
                  <div className="et-form-field">
                    <label>Note Title*</label>
                    <input name="title" type="text" required placeholder="Meeting Notes, Idea, Scratchpad..." className="et-input" autoFocus />
                  </div>
                  <div className="et-form-field">
                    <label>Content</label>
                    <textarea name="content" rows={6} placeholder="Write your note here..." className="et-input" style={{ resize: 'vertical' }} />
                  </div>
                </div>
              </div>
              <div className="card-drawer-footer et-drawer-footer">
                <button type="button" className="et-btn et-btn-secondary" onClick={() => setQuickNoteOpen(false)}>Cancel</button>
                <button type="submit" className="et-btn et-btn-primary">Save Note</button>
              </div>
            </form>
          </aside>
        </div>
      )}

      {/* ─── MODAL 5: QUICK ADD TASK DRAWER ─── */}
      {quickTaskOpen && (
        <div className="card-drawer-wrapper" role="presentation" style={{ zIndex: 100005 }}>
          <div className="card-drawer-overlay animate-fade-in" onClick={() => setQuickTaskOpen(false)} aria-hidden="true" />
          <aside className="card-drawer et-drawer animate-slide-left" role="dialog" aria-modal="true" aria-label="New Task">
            <div className="card-drawer-header et-drawer-header">
              <div className="card-drawer-header-left">
                <div className="card-drawer-icon-badge" style={{ background: 'rgba(139, 92, 246, 0.15)', color: '#8b5cf6' }}>
                  <CheckSquare size={18} />
                </div>
                <div className="card-drawer-titles">
                  <h2 className="card-drawer-title">New Task</h2>
                  <p className="card-drawer-subtitle">Add action item to task board</p>
                </div>
              </div>
              <button type="button" className="btn-icon" onClick={() => setQuickTaskOpen(false)}><X size={18} /></button>
            </div>

            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.target;
                const title = form.title.value.trim();
                const priority = form.priority.value;
                const due_date = form.due_date.value || undefined;

                try {
                  await api.post('/tasks', {
                    title,
                    priority,
                    due_date,
                    status: 'TODO',
                  });
                  showToast(`Task "${title}" created!`);
                  setQuickTaskOpen(false);
                  if (onRefreshData) onRefreshData();
                } catch {
                  window.location.href = `/apps/tasks?action=new&title=${encodeURIComponent(title)}`;
                }
              }}
              style={{ display: 'contents' }}
            >
              <div className="card-drawer-body et-drawer-body">
                <div className="et-form-grid">
                  <div className="et-form-field">
                    <label>Task Title*</label>
                    <input name="title" type="text" required placeholder="What needs to be done?" className="et-input" autoFocus />
                  </div>
                  <div className="et-form-field">
                    <label>Priority</label>
                    <select name="priority" className="et-select" defaultValue="MEDIUM">
                      <option value="LOW">Low</option>
                      <option value="MEDIUM">Medium</option>
                      <option value="HIGH">High</option>
                      <option value="URGENT">Urgent ⚡</option>
                    </select>
                  </div>
                  <div className="et-form-field">
                    <label>Due Date</label>
                    <input name="due_date" type="date" defaultValue={new Date().toISOString().split('T')[0]} className="et-input" />
                  </div>
                </div>
              </div>
              <div className="card-drawer-footer et-drawer-footer">
                <button type="button" className="et-btn et-btn-secondary" onClick={() => setQuickTaskOpen(false)}>Cancel</button>
                <button type="submit" className="et-btn et-btn-primary">Create Task</button>
              </div>
            </form>
          </aside>
        </div>
      )}
    </>
  );
}
